/**
 * 리뉴얼 홈페이지 콘텐츠 데이터.
 * 값은 회사소개서(Figma) 기준으로 정정된 최종본입니다.
 */
import { clientLogos, portfolioImages } from './renewalAssets';

/* ------------------------------------------------------------------ Strengths */

const MARQUEE_A = ['Java / Spring', 'Python', 'Node.js', 'React', 'Vue.js', 'TypeScript', 'Swift', 'Kotlin', 'Unity'];
const MARQUEE_B = ['Oracle', 'MSSQL', 'MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'AWS', 'Azure', 'GCP', 'KT Cloud', 'Docker', 'CI/CD'];

// width:max-content 위에서 translateX(-50%) 로 끊김 없이 순환시키려면 배열을 두 번 이어붙여야 합니다.
export const marqueeA = MARQUEE_A.concat(MARQUEE_A);
export const marqueeB = MARQUEE_B.concat(MARQUEE_B);

export const patternCatKeys = ['backend', 'frontend', 'mobile'];

export const patterns = {
  backend: {
    label: '백엔드 디자인 패턴',
    items: [
      {
        name: '싱글톤 패턴',
        desc: '애플리케이션 전체에서 인스턴스를 하나만 생성해 공유하는 패턴입니다. 자원 낭비를 막고 전역 상태를 일관되게 유지합니다.',
        usage: 'Spring Container가 관리하는 Bean의 기본 스코프로, 공통 설정·커넥션 풀 관리에 적용합니다.',
        stack: ['Java', 'Spring'],
      },
      {
        name: 'MVC',
        desc: 'Model(데이터·비즈니스 로직), View(화면), Controller(요청 처리)로 역할을 분리해 확장성과 유지보수성을 확보합니다.',
        usage: 'Spring MVC 기반 업무 시스템·관리자 화면 구축의 기본 구조로 사용합니다.',
        stack: ['Spring MVC', '역할 분리', '확장성'],
      },
      {
        name: '옵서버 패턴',
        desc: '객체의 상태 변화를 구독한 대상들에게 자동으로 통지하는 패턴입니다. 발신자와 수신자의 결합도를 낮춥니다.',
        usage: '이벤트 기반 시스템 — 알림 발송, 상태 변경 전파 로직에 적용합니다.',
        stack: ['이벤트 기반 시스템'],
      },
      {
        name: '전략 패턴',
        desc: '알고리즘을 인터페이스 뒤에 캡슐화해 실행 시점에 교체할 수 있게 합니다.',
        usage: '결제 수단·인증 방식처럼 동일한 흐름에서 알고리즘만 바뀌는 구간에 적용합니다.',
        stack: ['알고리즘 교체'],
      },
      {
        name: '어댑터 패턴',
        desc: '서로 다른 인터페이스를 가진 클래스를 연결해 함께 동작하도록 변환합니다.',
        usage: '레거시 시스템·외부 API 연동 시 인터페이스 호환 계층으로 사용합니다.',
        stack: ['인터페이스 호환'],
      },
      {
        name: '템플릿 메서드',
        desc: '알고리즘의 뼈대를 상위 클래스에 정의하고 세부 단계를 하위 클래스가 구현합니다.',
        usage: '공통 처리 흐름은 고정하고 업무별 처리만 달라지는 배치·리포트 생성에 적용합니다.',
        stack: ['알고리즘 구조 정의'],
      },
      {
        name: '팩토리 패턴',
        desc: '객체 생성 로직을 별도 팩토리에 위임해 생성과 사용을 분리합니다.',
        usage: 'Spring BeanFactory를 통한 객체 생성 관리에 적용합니다.',
        stack: ['BeanFactory', '객체 생성 관리'],
      },
      {
        name: '프록시 패턴',
        desc: '실제 객체에 대한 접근을 대리 객체가 제어하며 부가 기능을 덧붙입니다.',
        usage: 'Spring AOP로 트랜잭션·로깅·접근제어 같은 공통 관심사를 분리합니다.',
        stack: ['Spring AOP', '접근제어', '부가 기능'],
      },
      {
        name: '의존성 주입 (DI) · IoC',
        desc: '객체가 필요한 의존성을 직접 생성하지 않고 외부(컨테이너)에서 주입받는 방식입니다. 제어의 흐름이 역전됩니다.',
        usage: 'Spring Container가 @Component·@Bean으로 등록된 객체의 생명주기와 주입을 담당합니다.',
        stack: ['Spring Container', '@Component', '@Bean'],
      },
    ],
  },
  frontend: {
    label: '프론트 디자인 패턴',
    items: [
      {
        name: 'MVVM',
        desc: 'Model(데이터·비즈니스 로직), View(UI), ViewModel(UI 상태·데이터 변환)로 나누고 데이터 바인딩으로 연결합니다.',
        usage: 'React 기반 화면에서 Model은 API 호출·데이터 처리, View는 JSX·Props·렌더링 로직을 담당합니다.',
        stack: ['Model', 'View', 'ViewModel'],
      },
      {
        name: 'Function Component',
        desc: '클래스 대신 함수로 컴포넌트를 작성해 코드가 간결하고 Hooks와 자연스럽게 결합됩니다.',
        usage: 'React 구현의 기본 단위로, 모든 신규 화면을 함수형으로 작성합니다.',
        stack: ['React', 'JSX'],
      },
      {
        name: 'Custom Hooks',
        desc: '상태 관리와 비즈니스 로직을 컴포넌트 밖으로 추출해 재사용 가능한 단위로 만듭니다.',
        usage: 'ViewModel 역할을 맡아 UI 상태 관리·데이터 변환 로직을 화면 간 공유합니다.',
        stack: ['상태관리', '재사용 로직'],
      },
      {
        name: 'React Hooks',
        desc: '함수형 컴포넌트에서 상태와 생명주기를 다루는 API 집합입니다.',
        usage: 'useState(상태), useEffect(부수효과), useContext(공유상태)를 표준 조합으로 사용합니다.',
        stack: ['useState', 'useEffect', 'useContext'],
      },
    ],
  },
  mobile: {
    label: '모바일 아키텍처',
    items: [
      {
        name: 'MVVM (Android)',
        desc: 'Activity·Fragment는 UI만 담당하고, ViewModel이 UI 상태와 로직을 보유합니다.',
        usage: 'LiveData/StateFlow로 상태를 노출하고 Repository가 데이터소스를 관리합니다.',
        stack: ['Activity', 'ViewModel', 'LiveData'],
      },
      {
        name: 'MVI',
        desc: '사용자 이벤트(Intent) → 상태(Model) → 화면(View)의 단방향 흐름으로 상태를 예측 가능하게 만듭니다.',
        usage: 'StateFlow를 단일 진실 공급원으로 두고 View는 State를 구독만 합니다.',
        stack: ['Intent', 'StateFlow', '단방향'],
      },
      {
        name: 'Clean Architecture',
        desc: 'Presentation / Domain / Data 레이어를 분리해 의존성이 안쪽(도메인)으로만 향하게 합니다.',
        usage: 'Domain에 UseCase·Entity, Data에 Repository·DataSource를 배치합니다.',
        stack: ['Presentation', 'Domain', 'Data'],
      },
      {
        name: 'Multi Module',
        desc: '기능 단위로 모듈을 쪼개 빌드 속도와 팀 병렬 작업 효율을 높입니다.',
        usage: 'App / Domain / Data / Feature 모듈로 분리해 대규모 앱을 구성합니다.',
        stack: ['App Module', 'Feature Module'],
      },
      {
        name: 'VIPER (iOS)',
        desc: 'View, Interactor, Presenter, Entity, Router로 책임을 세분화한 iOS 아키텍처입니다.',
        usage: 'Router가 화면 전환, Presenter가 View와 Interactor의 조정자, Interactor가 UseCase를 처리합니다.',
        stack: ['View', 'Interactor', 'Presenter', 'Router'],
      },
      {
        name: 'MVVM (iOS)',
        desc: 'ViewController는 UI 업데이트만 담당하고 ViewModel이 비즈니스 로직을 보유합니다.',
        usage: 'Combine·RxSwift로 ViewModel과 View를 바인딩합니다.',
        stack: ['Combine', 'RxSwift'],
      },
      {
        name: 'Unity 아키텍처',
        desc: 'AR/VR 콘텐츠를 위한 씬·컴포넌트 기반 구조로, 렌더링과 상호작용 로직을 분리합니다.',
        usage: '온라인 국립과학관 메타버스 게임존, 의료 메타버스 등 VR/AR 프로젝트에 적용했습니다.',
        stack: ['Unity', 'AR', 'VR'],
      },
    ],
  },
};

export const methods = [
  {
    en: 'WATERFALL',
    ko: '워터풀 방법론',
    desc: '주로 공공기관 대상\n명확한 단계(요구사항 분석 → 설계 → 개발 → 테스트 → 배포)로 진행\n철저한 문서화와 계획 기반, 단계별 검증이 필수\n변경 사항이 적고 대규모 프로젝트에서 안정적으로 적용',
    output: '요구사항 명세서, 설계 문서, 개발 산출물 코드 및 기술문서',
  },
  {
    en: 'AGILE',
    ko: '애자일 방법론',
    desc: '짧은 개발 주기(Sprint)로 나누어 지속적으로 개발 및 개선\n고객 피드백을 반영하며 점진적으로 시스템을 완성\n변화 대응력이 뛰어나며 팀 협업과 커뮤니케이션을 중시',
    output: '백로그 리스트, 스프린트 계획서, 일일 스탠드업 미팅 노트, 스프린트 리뷰 및 회고 보고서',
  },
  {
    en: 'PROTOTYPE',
    ko: '프로토타입 개발 방법론',
    desc: '초기 단계에서 실제 동작하는 모델을 제작하여 피드백을 수집\n요구사항이 명확하지 않은 경우 사용자와의 협업을 통해 점진적으로 개발\n개발 초기에 요구사항을 조정할 수 있어 리스크를 줄일 수 있음',
    output: '프로토타입 모델, 사용자 피드백 문서, 요구사항 변경 내역, 기능 검증 보고서',
  },
  {
    en: 'PACKAGE',
    ko: '패키지 개발 방법론',
    desc: '사전에 설계된 패키지를 기반으로 개발\n범용적인 기능을 가진 패키지를 활용하여 개발 속도를 향상\n맞춤형 개발보다 비용과 시간이 절감됨',
    output: '패키지 적용 분석 보고서, 패키지 커스터마이징 문서, 통합 테스트 결과, 사용자 매뉴얼',
  },
  {
    en: 'CBD',
    ko: '컴포넌트 기반 개발',
    desc: '소프트웨어를 독립적인 컴포넌트 단위로 개발하여 조립하는 방식\n재사용성을 극대화하고 유지보수를 용이하게 함\n각 컴포넌트는 특정 기능을 담당하며, 필요에 따라 조합하여 시스템을 구성',
    output: '요구사항 정의, 컴포넌트 설계·개발·검증 산출물, 시스템 조립 및 재사용 명세',
  },
];

/* -------------------------------------------------------------------- Overview */

export const mission = [
  '혁신 기술로 지속 가능한 디지털 서비스 생태계를 구축',
  '사용자 중심의 직관적이고 효율적인 맞춤형 IT 솔루션을 설계',
  '비즈니스 성공과 사회적 가치를 동시에 창출하는 임팩트 중심 서비스를 제공',
];

export const values = [
  { no: '01', title: 'Integrity &\nDedication', desc: '코드 한줄, 프로젝트 하나하나에 책임감을 담아 최적의 결과물을 구현한다' },
  { no: '02', title: 'Trust &\nTransparency', desc: '투명한 개발 과정과 명확한 커뮤니케이션으로 고객과의 지속가능한 파트너십을 구축한다' },
  { no: '03', title: 'Rationality &\nEfficiency', desc: '논리적 사고와 효율적인 문제 해결을 기반으로 최적의 IT 솔루션을 제공한다' },
  { no: '04', title: 'Autonomy &\nResponsibility', desc: '창의적 문제 해결과 자기주도적 개발 문화를 통해 혁신적 솔루션을 창출한다' },
  { no: '05', title: 'Growth &\nInnovation', desc: '끊임없는 학습과 기술 트렌드 탐구로 개인의 역량과 회사의 경쟁력을 함께 높여간다' },
];

export const generalInfo = [
  { term: 'COMPANY', desc: '(주)비알아이티' },
  { term: 'CEO', desc: '박정훈' },
  { term: 'FOUNDED', desc: '2020년 1월 1일' },
  { term: 'BUSINESS', desc: 'SI/SM, 모바일 웹·앱 구축,\n솔루션 공급, IT 컨설팅', small: true, wide: true },
  {
    term: 'HEAD OFFICE',
    desc: '경기도 화성시 동탄구 동탄영천로 150, A동 1432호',
    sub: '(영천동, 현대실리콘엘리동탄)',
    wide: true,
    address: true,
  },
  { term: 'SEOUL BRANCH', desc: '서울특별시 영등포구 선유로 146, 이앤씨드림타워 610호', wide: true, address: true },
];

export const history = [
  { year: '2019', events: '12월 법인 설립', clients: '' },
  { year: '2020', events: '1월 BRIT 사업 시작', clients: '' },
  { year: '2021', events: '', clients: '스마트홈 산업 협회\n언론중재 위원회\n토로스 MOU' },
  {
    year: '2022',
    events: '10월 홍대 사옥 이전\n11월 특허 등록',
    clients: 'LG에너지솔루션\n자원순환센터\n유니에스\n현대위아',
  },
  { year: '2023', events: '', clients: '특허 인증\nLG에너지솔루션\n현대자동차\nDKI 의료 메타버스' },
  {
    year: '2024',
    events: '4월 연구소 설립\n10월 영등포 사옥 이전\n10월 한국 무역 협회 가입',
    clients: 'LG에너지솔루션\n현대자동차\n자원순환센터\nGS Global\n순복음교회\n이트론/카카오엔터프라이즈',
  },
  {
    year: '2025',
    events: '',
    clients: '산림과학원\n삼성SDS\nLG에너지솔루션\nLG전자\n조폐공사\n한화생명\nKT\n카카오엔터프라이즈',
  },
];

export const greeting = [
  '안녕하세요. BRIT(Benefit Renew Information Technology)입니다. BRIT는 혁신적인 IT 기술을 통해 더 나은 삶을 만들어가는 기업입니다.',
  '우리는 SI, SM, 솔루션 개발 및 적용, 인프라 구축 및 운영을 기반으로 고객에게 최적의 IT 서비스를 제공하며, 웹과 모바일을 아우르는 다양한 플랫폼에서 최상의 솔루션을 제공합니다.',
  '또한 BRIT는 혁신 기술을 통해 지속 가능한 서비스 창출을 목표로, 고객 중심의 맞춤형 IT 솔루션을 개발하고 있습니다. 이를 통해 고객의 성공을 지원할 뿐만 아니라, IT를 통해 사회적 가치를 함께 창출하는 기업으로 성장하고자 합니다.',
  '앞으로도 BRIT는 신뢰와 혁신을 바탕으로 더 나은 IT 환경을 만들어가고, 고객과 함께 발전하는 기업이 되겠습니다. 감사합니다.',
];

/* --------------------------------------------------------------------- Service */

export const basedTechnology = [
  { cat: 'Back-end', items: 'Java,  Python,  Node.js,  Vue.js,  TypeScript' },
  { cat: 'Front-end', items: 'React,  Vue.js,  TypeScript,  JavaScript,  HTML,  CSS' },
  { cat: 'Mobile', items: 'Swift(iOS),  Kotlin,  Java(Android),  Unity(AR, VR)' },
  { cat: 'Database', items: 'Oracle,  MSSQL,  MySQL,  PostgreSQL,  MongoDB,  Redis' },
  { cat: 'Infrastructure', items: 'AWS,  Azure,  GCP,  iCloud,  KT Cloud,  Naver,  공공클라우드 등' },
  { cat: 'etc.', items: 'CI/CD,  Git,  DevOps 관련 기술 보유' },
];

export const platformScope = [
  { no: '1', title: 'Web, App Development', desc: '웹 서비스 개발\n모바일 앱 개발\n반응형 웹 개발' },
  { no: '2', title: 'System Development', desc: '업무 시스템 구축\nERP/CRM 시스템\nAPI 개발 및 연동' },
  { no: '3', title: 'Cloud Service', desc: '클라우드 인프라 구축\n클라우드 마이그레이션\nDevOps 솔루션' },
  {
    no: '4',
    title: 'Management',
    desc: '각종 모니터링 관련 솔루션 구축 및 기술 지원\nWhatap, Grafana, ELK, Zabbix 솔루션 활용',
  },
];

export const solutionScope = [
  {
    no: '5',
    title: '외부 솔루션 사업',
    items: [
      { name: 'AEM (Adobe Experience Manager)', desc: '현대닷컴, LG닷컴' },
      { name: 'ESOL (API 기반 개발 솔루션)', desc: '코드아이티 솔루션' },
      {
        name: '인프라 (DevOps 환경 구축)',
        desc: '클라우드 관련 구축 및 MSP — AWS, iCloud, KT Cloud 등 국내·해외 클라우드 지원',
      },
    ],
  },
  {
    no: '6',
    title: '자체 솔루션 사업',
    items: [
      { name: 'PT 관련 예약 시스템', desc: 'Fitime — ', link: { href: 'https://fitime.br-it.xyz/login', label: 'fitime.br-it.xyz' } },
      { name: 'CMS 통합 솔루션', desc: 'NodePlatform' },
      { name: '웹기반 채팅 솔루션', desc: '' },
    ],
  },
];

/* ------------------------------------------------------------------ Portfolios */

export const featured = [
  {
    img: portfolioImages.fod,
    alt: '현대차그룹 FoD',
    client: '현대차그룹',
    title: 'FoD 개발',
    desc: '전기차에서 사용되는 자동차용 앱 스토어\n백엔드 연동 및 관리시스템 개발',
    url: '',
    urlLabel: '',
  },
  {
    img: portfolioImages.lges,
    alt: 'LG에너지솔루션 온라인 전시관',
    client: 'LG에너지솔루션',
    title: '디지털 전시 플랫폼 · 가상라인투어 · 표준운영 리포트',
    desc: '디지털 전시 및 전시회(아카이브성) VR 개발 및 관리자 시스템 개발\n생산라인을 실사 촬영 후 VR로 조회할 수 있는 시스템 개발 (대외비)\n생산라인 점검일지 모바일 앱 및 관리자 시스템 전체 — 한/영/중/폴란드/러시아/우크라이나 지원',
    url: 'https://virtual.lgensol.com/index.html',
    urlLabel: 'virtual.lgensol.com',
  },
  {
    img: portfolioImages.hyundaiwia,
    alt: '현대위아 디지털 전시 플랫폼',
    client: '현대위아',
    title: '디지털전시 플랫폼 개발',
    desc: '현대위아의 기술(열관리, 엔진&모듈)력부터 ESG, 라이브러리까지\n기업의 히스토리를 공유하고 아카이빙하는 사이트',
    url: 'https://virtual-autoparts.hyundai-wia.com/',
    urlLabel: 'virtual-autoparts.hyundai-wia.com',
  },
  {
    img: portfolioImages.science,
    alt: '온라인 국립과학관',
    client: '국립 과학관',
    title: '온라인 국립 과학관 개발',
    desc: '특별기획 전시회를 실사 촬영하여 VR로 구현, 관리 시스템 포함 (1~3차)\nUnity 메타버스 게임존 개발',
    url: 'https://vr2023.science.go.kr/intro/index.html',
    urlLabel: 'vr2023.science.go.kr',
  },
  {
    img: portfolioImages.ktrbrain,
    alt: 'KT R-Brain',
    client: 'KT',
    title: 'R-Brain',
    desc: 'R-Brain 로봇관제시스템 백엔드 개발',
    url: '',
    urlLabel: '',
  },
  {
    img: portfolioImages.gsgev,
    alt: 'GS글로벌 전기트럭',
    client: 'GS글로벌',
    title: '전기트럭 홈페이지 구축',
    desc: '전기트럭 홈페이지 프론트/백엔드 개발',
    url: 'https://gsgev.com/',
    urlLabel: 'gsgev.com',
  },
  {
    img: portfolioImages.unies,
    alt: '유니에스 홈페이지',
    client: '유니에스',
    title: '홈페이지, 채용시스템 고도화',
    desc: '유니에스 회사 홈페이지 및 채용시스템 고도화',
    url: 'https://www.unies.com/web/main/home/list',
    urlLabel: 'unies.com',
  },
  {
    img: portfolioImages.smarthome,
    alt: '스마트홈 산업협회 사이버 전시관',
    client: '스마트홈 산업협회',
    title: '사이버 전시관 개발',
    desc: 'VR 기반의 가상 전시관 플랫폼\n업체별로 공간 및 제품을 선택하면 해당 공간에 전시된 제품 조회',
    url: '',
    urlLabel: '',
  },
];

export const industries = [
  {
    name: '제조 및 자동차 산업',
    items: [
      { title: '현대위아 디지털 전시 플랫폼 (2021)', desc: 'VR 기반의 가상 전시 플랫폼 구축\nhttps://virtual-autoparts.hyundai-wia.com/' },
      { title: '현대차그룹 FoD 개발 (2022)', desc: '전기차용 앱스토어 백엔드 연동 및 관리 시스템 개발' },
      { title: '기아닷컴 고도화 백엔드 개발 (2022)', desc: '기아 공식 웹사이트 백엔드 고도화' },
      { title: 'AEM 기반 현대차 IR 사이트 구축 (2023)', desc: '현대자동차 IR 관련 웹사이트 개발\nhttps://www.hyundai.com/worldwide/ko/company/ir' },
    ],
  },
  {
    name: '에너지 및 환경 산업',
    items: [
      { title: 'LG에너지솔루션 디지털 전시 플랫폼 개발 (2021)', desc: 'VR 기반 디지털 전시 및 관리자 시스템 구축\nhttps://virtual.lgensol.com' },
      { title: 'LG에너지솔루션 가상라인투어 시스템 (2022)', desc: '생산라인 실사 촬영 후 VR 기반 조회 시스템 개발' },
      { title: 'LG에너지솔루션 표준운영 Report 개발 (2022)', desc: '글로벌 생산라인 점검 앱 및 관리자 시스템 구축\n(한/영/중/폴란드/러시아/우크라이나 지원)' },
      { title: 'LG에너지솔루션 Interbattery, ees Europe 온라인 전시관 (2023)', desc: '글로벌 배터리 전시회 온라인 플랫폼 구축' },
      { title: '삼성SDS 온실가스관리 솔루션 구축 (2024)', desc: '삼성SDS의 자체 솔루션 확산 지원' },
    ],
  },
  {
    name: '금융 및 공공기관',
    items: [
      { title: '자원순환센터 1회용컵 보증금 모바일 개발 (2021)', desc: "보증금 반환 시스템의 모바일 앱 개발 및 운영\nGoogle Play Store에서 '자원순환보증금' 검색" },
      { title: '온라인 국립과학관 1~3차 개발 (2022)', desc: '국립 과학관의 특별 전시회 실사 촬영 및 VR 구현\nhttps://vr2023.science.go.kr' },
      { title: '국립국제교육원 차세대 한국유학종합시스템 VR 박람회 개발 (2022)', desc: 'VR 기반 온라인 유학 박람회 시스템 구축' },
      { title: '조폐공사 지급결제 시스템 모바일 구축 (2024)', desc: '하이브리드 방식의 모바일 앱 개발' },
      { title: '산림과학원 클라우드 전환 2차 수행 (2024)', desc: '산림과학원의 서버를 대구클라우드센터로 이관 및 구축' },
    ],
  },
  {
    name: 'IT 및 기술 서비스',
    items: [
      { title: '유니에스 홈페이지 및 채용 시스템 고도화 (2021)', desc: '유니에스 공식 웹사이트 및 채용 시스템 개발\nhttps://www.unies.com/ · https://www.jobunies.com/' },
      { title: 'LG닷컴 AEM 운영 (2023)', desc: 'AEM 기반 글로벌 사이트 운영 및 개선' },
      { title: 'GS글로벌 전기트럭 홈페이지 구축 (2023)', desc: '전기트럭 관련 웹사이트 개발\nhttps://gsgev.com/' },
      { title: '한화생명 비대면 인증 고도화 (2024)', desc: '비대면 인증 시스템 강화 및 개발' },
      { title: 'KT R-Brain 개발 (2024)', desc: '로봇 정보 관리 시스템을 Vue.js에서 React로 변환 및 고도화' },
    ],
  },
  {
    name: '기타 산업',
    items: [
      { title: '스마트홈산업협회 사이버전시관 개발 (2021)', desc: 'VR 기반의 가상 전시관, 업체별 제품 조회 기능 개발' },
      { title: '웅진북클럽 영어 플랫폼 운영 (2021)', desc: '웅진북클럽의 온라인 교육 플랫폼 운영 지원' },
      { title: 'DKI 개방형 의료 메타버스 개발 (2022)', desc: '의료기기 관련 VR 플랫폼 개발\nhttps://dki-www.kvrcms.com\n맘브러시(유아치아관리 게임) AR 게임 개발' },
      { title: '한국일보 (2024)', desc: 'Korea Times 홈페이지 고도화\n(UI/UX 변경, 프론트엔드 개선, 모바일 시스템 개편)' },
    ],
  },
];

export const clients = [
  { name: 'LG', src: clientLogos.lg },
  { name: 'LG전자', src: clientLogos.lgelec },
  { name: 'LG CNS', src: clientLogos.lgcns },
  { name: '삼성SDS', src: clientLogos.samsung },
  { name: 'GS Global', src: clientLogos.gs },
  { name: '현대위아', src: clientLogos.hyundaiwia },
  { name: 'KT', src: clientLogos.kt },
  { name: '한국조폐공사', src: clientLogos.komsco },
  { name: '한화생명', src: clientLogos.hanwhalife },
  { name: '국립국제교육원', src: clientLogos.niie },
  { name: '국립과학관', src: clientLogos.nsm },
  { name: '국립산림과학원', src: clientLogos.salim },
  { name: '유니에스', src: clientLogos.unies },
  { name: '웅진북클럽', src: clientLogos.woongjin },
  { name: 'DKI', src: clientLogos.dki },
  { name: '한국일보', src: clientLogos.hankuk },
  { name: 'COSMO 자원순환보증금관리센터', src: clientLogos.cosmo },
];

/* ---------------------------------------------------------------------- Common */

export const company = {
  name: '(주)비알아이티',
  ceo: '박정훈',
  headOffice: '경기도 화성시 동탄구 동탄영천로 150, A동 1432호 (영천동, 현대실리콘엘리동탄)',
  branch: '서울특별시 영등포구 선유로 146, 이앤씨드림타워 610호',
  site: 'https://www.br-it.co.kr/',
  siteLabel: 'www.br-it.co.kr',
  email: 'info@br-it.co.kr',
  saramin:
    'https://www.saramin.co.kr/zf_user/company-info/view-inner-recruit?csn=SnhWM1pXZHlyQ1VkbU1LUHFqYmo5dz09',
};

/** CONTACT 지도 탭. 구글 임베드는 흑백 필터를 씌우고 컬러 핀을 오버레이합니다. */
const mapQuery = query => ({
  embed: `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=17&hl=ko&output=embed`,
  naver: `https://map.naver.com/p/search/${encodeURIComponent(query)}`,
});

export const offices = [
  {
    key: 'head',
    tabLabel: '본점 · 화성 동탄',
    eyebrow: 'HEAD OFFICE / 본점',
    lines: ['경기도 화성시 동탄구 동탄영천로 150,', 'A동 1432호'],
    note: '(영천동, 현대실리콘엘리동탄)',
    mapTitle: 'BRIT 본점 위치 지도',
    ...mapQuery('경기도 화성시 동탄영천로 150'),
  },
  {
    key: 'branch',
    tabLabel: '지점 · 서울 영등포',
    eyebrow: 'SEOUL BRANCH / 지점',
    lines: ['서울특별시 영등포구 선유로 146,', '이앤씨드림타워 610호'],
    note: '',
    mapTitle: 'BRIT 서울 지점 위치 지도',
    ...mapQuery('서울특별시 영등포구 선유로 146'),
  },
];

export const navMenu = [
  { id: 'overview', label: 'OVERVIEW' },
  { id: 'strengths', label: 'STRENGTHS' },
  { id: 'service', label: 'SERVICE' },
  { id: 'portfolios', label: 'PORTFOLIOS' },
  { id: 'contact', label: 'CONTACT' },
];
